import * as React from "react";
import { Stack, Typography, Chip, Link, Box } from "@mui/material";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import { Link as RouterLink } from "react-router-dom";
import { colors } from "../../utils";
import { helpSourceHref, linkifyParts } from "./helpers";

// Renders answer text with support@iyuga.net / URLs as real <a> nodes; every
// text run stays a React-escaped text node (no innerHTML).
function RichText({ text }) {
  const parts = linkifyParts(text);
  return (
    <>
      {parts.map((part, i) => {
        if (part.type === "email") {
          return (
            <Link
              key={i}
              href={`mailto:${part.value}`}
              sx={{ color: colors.PrimaryPurple700, fontWeight: 600 }}
            >
              {part.value}
            </Link>
          );
        }
        if (part.type === "url") {
          return (
            <Link
              key={i}
              href={part.value}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: colors.PrimaryPurple700, fontWeight: 600 }}
            >
              {part.value}
            </Link>
          );
        }
        return <React.Fragment key={i}>{part.value}</React.Fragment>;
      })}
    </>
  );
}

/**
 * A single chat turn. `role` "user" → navy bubble aligned right; "assistant" →
 * light bubble aligned left with optional source chips. Answer text is linkified
 * so the "don't-know → support" pointer is clickable. Source chips are links to
 * the cited Help article (T22.440: they used to look clickable but did nothing).
 */
export default function MessageBubble({
  role,
  content,
  images = [],
  sources = [],
  onSourceClick = () => {},
}) {
  const isUser = role === "user";
  return (
    <Stack
      alignItems={isUser ? "flex-end" : "flex-start"}
      sx={{ width: "100%" }}
    >
      <Box
        sx={{
          maxWidth: "85%",
          px: 1.5,
          py: 1,
          borderRadius: isUser ? "12px 12px 2px 12px" : "12px 12px 12px 2px",
          backgroundColor: isUser ? colors.primary : colors.Highlight,
          color: isUser ? colors.white : colors.primary,
          border: isUser ? "none" : `1px solid ${colors.PrimaryPurple100}`,
        }}
      >
        {images && images.length > 0 && (
          <Stack
            direction="row"
            flexWrap="wrap"
            gap={0.75}
            sx={{ mb: content ? 0.75 : 0 }}
          >
            {images.map((src, i) => (
              <Box
                key={i}
                component="img"
                src={src}
                alt={`attachment ${i + 1}`}
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "6px",
                  objectFit: "cover",
                  border: "1px solid rgba(255,255,255,0.5)",
                }}
              />
            ))}
          </Stack>
        )}
        {content ? (
          <Typography
            component="div"
            sx={{
              fontSize: 13,
              lineHeight: 1.5,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            <RichText text={content} />
          </Typography>
        ) : null}
      </Box>

      {!isUser && sources && sources.length > 0 && (
        <Stack
          direction="row"
          flexWrap="wrap"
          gap={0.5}
          sx={{ mt: 0.75, maxWidth: "85%" }}
        >
          {sources.map((s, i) => (
            <Chip
              key={i}
              size="small"
              clickable
              component={RouterLink}
              to={helpSourceHref(s)}
              onClick={onSourceClick}
              icon={<SupportAgentIcon sx={{ fontSize: 14 }} />}
              label={
                s.category ? `${s.category} — ${s.title}` : s.title || "Help"
              }
              sx={{
                height: 22,
                maxWidth: "100%",
                backgroundColor: colors.white,
                border: `1px solid ${colors.PrimaryPurple300}`,
                color: colors.primary,
                fontSize: 11,
                "&:hover": { backgroundColor: colors.PrimaryPurple100 },
                "& .MuiChip-icon": { color: colors.PrimaryPurple700 },
                "& .MuiChip-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
}
